import {
  EstimateFeeType,
  sessionEstimateItemTitles,
  TravelActivityTypes,
  WorkActivityTypes,
  WorkTimeType,
} from '@shared/types';
import { getWorkTimeType } from '@shared/utils';
import {
  Estimate,
  EstimateService,
  EstimateStatus,
  SessionActivity,
  SessionEstimate,
  SessionEstimateItem,
  SessionEstimateItemService,
  SessionEstimateItemUnitType,
  SessionEstimateService,
  TCommercialId,
  TCreateUsageEstimateItemRequest,
  TEstimateId,
  TProjectId,
  TRoleId,
  TSessionActivityId,
  TSessionEstimateId,
  TSessionEstimateItemId,
  TSessionId,
  TUpdateEstimateRequest,
  TUpdateUsageEstimateItemRequest,
  TUsageEstimateId,
  TUsageEstimateItemId,
  TUsageId,
  UsageEstimate,
  UsageEstimateItem,
  UsageEstimateItemService,
  UsageEstimateService,
} from '@talon/web/lib/api';
import { DateString, Nullable } from '@talon/web/src/types';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from './baseStore'; // for some dumb reason, these have to be imported individually
import { RootStore } from './rootStore'; // for some dumb reason, these have to be imported individually
import dayjs from 'dayjs';

const estimateRelations = 'session_estimates,usage_estimates,project';

export class EstimateStore extends BaseStore {
  currentEstimate: Estimate | undefined = undefined;
  estimateList: Estimate[] = [];
  isLoading = false;
  isSaving = false;

  // Temp Estimates (used by Session and Usage Estimators for live estimates)
  tempEstimate: Estimate | undefined = undefined;
  tempSessionEstimates: Record<TSessionId, SessionEstimate> = {};
  tempSessionEstimateItems: Record<TSessionId, Record<TSessionActivityId, SessionEstimateItem[]>> = {};
  tempUsageEstimates: Record<TCommercialId, UsageEstimate> = {};
  tempUsageEstimateItems: Record<TCommercialId, Record<TUsageId, UsageEstimateItem[]>> = {};

  // Quick Estimate
  quickEstimateTotal = 0;
  quickEstimateTotalPlusFringe = 0;

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {
      currentEstimate: observable,
      estimateList: observable,
      isLoading: observable,
      isSaving: observable,
      tempEstimate: observable,
      tempSessionEstimates: observable,
      tempSessionEstimateItems: observable,
      tempUsageEstimates: observable,
      tempUsageEstimateItems: observable,
      quickEstimateTotal: observable,
      quickEstimateTotalPlusFringe: observable,
      canEditCurrent: computed,
      lastSessionEstimateDate: computed,
      setCurrentEstimate: action,
      setTempEstimate: action,
      setEstimateList: action,
    });
  }

  setIsSaving(isSaving: boolean) {
    runInAction(() => (this.isSaving = isSaving));
  }

  /**
   * Estimates
   */

  get canEditCurrent(): boolean {
    return !!this.currentEstimate?.project_id && this.currentEstimate?.status === EstimateStatus.Draft;
  }

  setCurrentEstimate(estimate?: Estimate) {
    this.currentEstimate = estimate;
  }

  setEstimateList(estimateList: Estimate[]) {
    this.estimateList = estimateList;
  }

  listEstimates = async (projectId: TProjectId, relations?: string) => {
    if (!projectId) return;
    // TODO implement pagination
    try {
      runInAction(() => (this.isLoading = true));

      const { estimates } = await this.q(
        EstimateService.listEstimates(projectId, undefined, undefined, undefined, relations)
      );
      runInAction(() => {
        this.setEstimateList([...estimates].sort((a: Estimate, b: Estimate) => (a.created_at < b.created_at ? 1 : -1)));
        this.isLoading = false;
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  getEstimate = async (estimateId?: TEstimateId, relations?: string) => {
    if (!estimateId) return;
    try {
      const { estimate } = await this.q(EstimateService.getEstimate(estimateId, relations));
      return estimate as Estimate;
    } catch (e) {
      this.handleError(e);
    }
  };

  getEstimateByTitle = async (title?: string, relations?: string, isHidden?: boolean) => {
    if (!title) return;
    try {
      const { estimate } = await this.q(
        EstimateService.getEstimateByTitle(title, relations, isHidden ? 'true' : 'false')
      );
      return estimate as Estimate;
    } catch (e) {
      this.handleError(e);
    }
  };

  getEstimateTotals = async (estimateId?: TEstimateId) => {
    if (!estimateId) return;
    try {
      const totals = await this.q(EstimateService.getEstimateTotals(estimateId));
      return totals;
    } catch (e) {
      this.handleError(e);
    }
  };

  refreshEstimateTotals = async (estimateId?: TEstimateId) => {
    if (!estimateId) return;
    try {
      const totals = await this.getEstimateTotals(estimateId);
      if (totals) {
        const estimateIndex = this.estimateList.findIndex((e) => e.id === estimateId);
        if (estimateIndex > -1) {
          const list = this.estimateList;
          list[estimateIndex] = {
            ...list[estimateIndex],
            total: totals.total,
            total_plus_fringe: totals.total_plus_fringe,
          };
          this.setEstimateList(list);
        }

        if (this.currentEstimate?.id === estimateId)
          runInAction(() => {
            this.setCurrentEstimate({
              ...this.currentEstimate,
              total: totals.total,
              total_plus_fringe: totals.total_plus_fringe,
            } as Estimate);
          });
      }
    } catch (e) {
      this.handleError(e);
    }
  };

  refreshEstimate = async (estimateId?: TEstimateId) => {
    if (!estimateId) return;
    try {
      const { estimate: updatedEstimate } = await this.q(EstimateService.getEstimate(estimateId, estimateRelations));
      if (!updatedEstimate) return;
      const estimateIndex = this.estimateList.findIndex((e) => e.id === estimateId);
      if (estimateIndex > -1) {
        const list = [...this.estimateList];
        list[estimateIndex] = updatedEstimate;
        this.setEstimateList(list);
      }

      if (this.currentEstimate?.id === estimateId) {
        runInAction(() => {
          this.setCurrentEstimate(updatedEstimate);
        });
      }
    } catch (e) {
      this.handleError(e);
    }
  };

  createEstimate = async (projectId: TProjectId, title?: string, isHidden?: boolean) => {
    if (!projectId) return;
    try {
      this.setIsSaving(true);
      const { estimate: newEstimate } = await this.q(
        EstimateService.createEstimate({
          projectId,
          title,
          isHidden,
        })
      );
      const estimate = await this.getEstimate(newEstimate?.id, estimateRelations);
      if (estimate) {
        runInAction(() => {
          this.estimateList.unshift(estimate);
        });
      }
      return estimate;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  recalculateEstimate = async (e: Estimate) => {
    const i = this.estimateList?.findIndex((p) => p.id === e.id);
    if (i === undefined || i === -1) return;
    try {
      const { estimate } = await this.q(EstimateService.recalculateEstimate(e.id));
      const updatedEstimate = await this.getEstimate(estimate?.id, estimateRelations);
      if (updatedEstimate) {
        runInAction(() => {
          this.estimateList.splice(i, 1, updatedEstimate);
          this.setCurrentEstimate(updatedEstimate);
        });
      }
    } catch (e) {
      this.handleError(e);
    }
  };

  updateEstimate = async (e: Estimate) => {
    const i = this.estimateList?.findIndex((p) => p.id === e.id);
    if (i === undefined || i === -1) return;
    const updateRequest: TUpdateEstimateRequest = {
      status: e.status,
      title: e.title,
    };
    try {
      this.setIsSaving(true);
      const { estimate } = await this.q(EstimateService.updateEstimate(e.id, updateRequest));
      const updatedEstimate = await this.getEstimate(estimate?.id, estimateRelations);
      if (updatedEstimate) {
        runInAction(() => {
          this.estimateList.splice(i, 1, updatedEstimate);
          this.setCurrentEstimate(updatedEstimate);
        });
      }
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  deleteEstimate = async (estimateId: string) => {
    try {
      this.setIsSaving(true);
      await this.q(EstimateService.deleteEstimate(estimateId));
      if (estimateId === this.currentEstimate?.id) this.setCurrentEstimate(undefined);

      runInAction(() => {
        this.estimateList = this.estimateList?.filter((p) => p.id !== estimateId);
      });
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  quickEstimate = async (
    performerCounts: Record<string, number>,
    workHours: number,
    hasPriorDayFitting: boolean,
    isVoiceOver: boolean
  ) => {
    try {
      const hasPerformerCounts = Object.values(performerCounts).findIndex((count) => count > 0) > -1;

      let total = 0;
      let totalPlusFringe = 0;

      if (hasPerformerCounts && workHours) {
        const res = await this.q(
          SessionEstimateService.quickSessionEstimate({
            performerCounts: JSON.stringify(performerCounts),
            workHours,
            hasPriorDayFitting,
            isVoiceOver,
          })
        );

        total = res.total;
        totalPlusFringe = res.totalPlusFringe;
      }

      runInAction(() => {
        this.quickEstimateTotal = total;
        this.quickEstimateTotalPlusFringe = totalPlusFringe;
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  /**
   * Temp Estimates
   *
   * - Hidden Estimates for each Project
   * - Used for live estimation on the Session & Usage Estimators
   * - Created when the Estimators are used for the first time
   */

  setTempEstimate = (estimate?: Estimate) => (this.tempEstimate = estimate);

  getTempSessionEstimate = (sessionId?: TSessionId) => (sessionId ? this.tempSessionEstimates[sessionId] : undefined);
  getTempSessionEstimateItems = (sessionId?: TSessionId, activityId?: TSessionActivityId) =>
    sessionId && activityId ? this.tempSessionEstimateItems[sessionId]?.[activityId] : undefined;

  getTempUsageEstimate = (commercialId?: TCommercialId) =>
    commercialId ? this.tempUsageEstimates[commercialId] : undefined;
  getTempUsageEstimateItems = (commercialId?: TCommercialId, usageId?: TUsageId) =>
    commercialId && usageId ? this.tempUsageEstimateItems[commercialId]?.[usageId] : undefined;

  refreshTempEstimate = async (skipSessions = false, skipUsages = false) => {
    if (!this.tempEstimate || this.isLoading) return;
    try {
      runInAction(() => (this.isLoading = true));

      const estimateId = this.tempEstimate.id;
      const { estimate: updatedEstimate } = await this.q(EstimateService.getEstimate(estimateId, estimateRelations));
      if (!updatedEstimate) return;

      if (!skipSessions) {
        for (const sessionEstimate of updatedEstimate.session_estimates) {
          const session = sessionEstimate.session;
          if (!session) continue;

          const timestep = 0.25;
          let processedBaseFee = false;

          const estimateItems: Record<TSessionActivityId, SessionEstimateItem[]> = {};
          const activitiesByRole: Record<TRoleId, SessionActivity[]> = {};

          session?.activities.forEach((activity) => {
            activity.roles.forEach((role) => {
              activitiesByRole[role.id] ??= [];
              activitiesByRole[role.id].push(activity);
            });
          });

          for (const roleId of Object.keys(activitiesByRole)) {
            const activities = activitiesByRole[roleId].sort((a, b) =>
              dayjs(a.start_time ?? 0).diff(b.start_time ?? 0)
            );

            const items = sessionEstimate?.items.filter((i) => i.role_id === roleId);
            const workTimeItems: Record<string, SessionEstimateItem | undefined> = {
              [WorkTimeType.StraightTime]: items?.find((i) =>
                i.title
                  .toLocaleLowerCase()
                  .includes(sessionEstimateItemTitles[WorkTimeType.StraightTime].toLocaleLowerCase())
              ),
              [WorkTimeType.TimeAndOneHalf]: items?.find((i) =>
                i.title
                  .toLocaleLowerCase()
                  .includes(sessionEstimateItemTitles[WorkTimeType.TimeAndOneHalf].toLocaleLowerCase())
              ),
              [WorkTimeType.DoubleTime]: items?.find((i) =>
                i.title
                  .toLocaleLowerCase()
                  .includes(sessionEstimateItemTitles[WorkTimeType.DoubleTime].toLocaleLowerCase())
              ),
            };

            let totalHours = 0;

            for (const a of activities) {
              estimateItems[a.id] ??= [];

              if (!a.hours) continue;

              // Get work time
              if (WorkActivityTypes.has(a.type)) {
                const h = a.hours ?? 0;

                const estimates: Record<string, SessionEstimateItem | undefined> = {
                  [WorkTimeType.StraightTime]: undefined,
                  [WorkTimeType.TimeAndOneHalf]: undefined,
                  [WorkTimeType.DoubleTime]: undefined,
                };

                for (let i = 0; i < h; i += timestep) {
                  totalHours += timestep;
                  const workTimeType = getWorkTimeType(totalHours);

                  const workTimeItem = workTimeItems[workTimeType];
                  if (workTimeItem) {
                    let estimate = estimates[workTimeType];
                    if (!estimate || typeof estimate.units !== 'number') {
                      estimate = { ...workTimeItem, units: 0 };
                      estimates[workTimeType] = estimate;
                    }
                    estimate.units = (estimate.units ?? 0) + timestep;

                    const currentUnits = estimate.units ?? 0;
                    const originalUnits = workTimeItem.units ?? 0;

                    if (originalUnits) {
                      const multiplier = currentUnits / originalUnits;
                      estimate.subtotal = workTimeItem.subtotal * multiplier;
                      estimate.subtotal_plus_fringe = workTimeItem.subtotal_plus_fringe * multiplier;
                    }
                  }
                }

                estimateItems[a.id].push(
                  ...Object.values(estimates).filter((e): e is SessionEstimateItem => !!e)
                );
              } else {
                const matches =
                  sessionEstimate?.items.filter(
                    (item) =>
                      item.unit_type === SessionEstimateItemUnitType.Hours &&
                      item.role_id === roleId &&
                      (item.session_activity?.type === a.type ||
                        item.title.toLocaleLowerCase().includes(a.type.toLocaleLowerCase()))
                  ) ?? [];

                const totalUnits =
                  (!matches[1]
                    ? matches[0]?.units
                    : matches.map((item) => item.units ?? 0).reduce((acc, hours) => (acc ?? 0) + hours)) ?? 0;

                for (const match of matches) {
                  const auditionEstimateItem: SessionEstimateItem = { ...match };

                  const currentValue = a.hours ?? 0;
                  const originalValue = totalUnits ?? 0;

                  if (currentValue) {
                    const multiplier = currentValue / originalValue;
                    auditionEstimateItem.units = currentValue;
                    auditionEstimateItem.subtotal *= multiplier;
                    auditionEstimateItem.subtotal_plus_fringe *= multiplier;
                  }

                  estimateItems[a.id].push(auditionEstimateItem);
                }
              }

              // Look for any Base Session Fee estimate items and add them
              if (!processedBaseFee) {
                const baseFeeTitle = 'Base Session Fee'.toLocaleLowerCase();

                const matches =
                  sessionEstimate?.items.filter(
                    (item) => item.role_id === roleId && item.title.toLocaleLowerCase().includes(baseFeeTitle)
                  ) ?? [];

                if (matches.length) estimateItems[a.id].push(...matches);
                processedBaseFee = true;
              }

              // If it's a travel day, check for No Service Day items
              if (TravelActivityTypes.has(a.type)) {
                const matches =
                  sessionEstimate?.items.filter((item) => {
                    if (item.role_id !== roleId) return false;
                    const title = item.title.toLocaleLowerCase();

                    if (title.includes('No Service Day'.toLocaleLowerCase())) {
                      return true;
                    } else if (title.includes(EstimateFeeType.StudioZoneTravelFee.toLocaleLowerCase())) {
                      return true;
                    } else {
                      return false;
                    }
                  }) ?? [];

                if (matches.length) estimateItems[a.id].push(...matches);
              }

              // Add any associated premiums
              const premiums =
                sessionEstimate?.items.filter(
                  (item) =>
                    item.role_id === roleId &&
                    item.title
                      .toLocaleLowerCase()
                      .includes((sessionEstimateItemTitles[a.type] ?? a.type).toLocaleLowerCase())
                ) ?? [];

              if (premiums.length) estimateItems[a.id].push(...premiums);
            }

            // Non-Activity fees
            for (const item of sessionEstimate.items) {
              if (item.unit_type === SessionEstimateItemUnitType.Count) {
                let totalHours = 0;
                const itemActivities =
                  activities.filter((activity) => {
                    if (
                      activity.roles?.find((role) => role.id === roleId) &&
                      (activity.type === item.session_activity?.type ||
                        item.title
                          .toLocaleLowerCase()
                          .includes((sessionEstimateItemTitles[activity.type] || activity.type)?.toLocaleLowerCase()))
                    ) {
                      totalHours += activity.hours ?? 0;
                      return true;
                    }
                  }) ?? [];

                for (const activity of itemActivities) {
                  const sessionEstimateItem: SessionEstimateItem = {
                    ...item,
                    session_activity: activity,
                    session_activity_id: activity.id,
                  };

                  if (totalHours) {
                    const multiplier = (activity.hours ?? 0) / totalHours;
                    sessionEstimateItem.units = activity.hours ?? 0;
                    sessionEstimateItem.subtotal *= multiplier;
                    sessionEstimateItem.subtotal_plus_fringe *= multiplier;
                  }

                  estimateItems[activity.id].push(sessionEstimateItem);
                }
              }
            }
          }

          runInAction(() => {
            this.tempSessionEstimates[session.id] = sessionEstimate;
            this.tempSessionEstimateItems[session.id] = estimateItems;
          });
        }
      }

      if (!skipUsages) {
        const estimateItems: Record<TCommercialId, Record<TUsageId, UsageEstimateItem[]>> = {};
        for (const usageEstimate of updatedEstimate.usage_estimates) {
          if (!usageEstimate.commercial) continue;

          estimateItems[usageEstimate.commercial.id] ??= {};

          for (const item of usageEstimate.items) {
            estimateItems[usageEstimate.commercial.id][item.usage.id] ??= [];
            estimateItems[usageEstimate.commercial.id][item.usage.id].push(item);
          }

          runInAction(() => {
            if (usageEstimate.commercial) this.tempUsageEstimates[usageEstimate.commercial.id] = usageEstimate;
          });
        }

        runInAction(() => {
          this.tempUsageEstimateItems = estimateItems;
        });
      }

      runInAction(() => {
        this.tempEstimate = updatedEstimate;
      });
    } catch (e) {
      this.handleError(e);
    } finally {
      runInAction(() => (this.isLoading = false));
    }
  };

  /**
   * SessionEstimates
   */

  get lastSessionEstimateDate(): DateString | undefined {
    return (
      this.currentEstimate?.session_estimates.slice().sort((a, b) => ((a.date ?? '') > (b.date ?? '') ? -1 : 1))[0]
        ?.date ?? ''
    );
  }

  getSessionEstimate = async (estimateId?: TEstimateId, relations?: string) => {
    if (!estimateId) return;
    try {
      const { estimate } = await this.q(EstimateService.getEstimate(estimateId, relations));
      return estimate as Estimate;
    } catch (e) {
      this.handleError(e);
    }
  };

  getSessionEstimateDates = (sessionEstimateId?: TSessionEstimateId): DateString[] => {
    return (this.currentEstimate?.session_estimates
      .filter((se) => se.id !== sessionEstimateId)
      .slice()
      .sort((a, b) => ((a.date ?? '') > (b.date ?? '') ? -1 : 1))
      .map((se) => se.date ?? '') ?? []) as DateString[];
  };

  createSessionEstimate = async (
    title = 'New SessionEstimate',
    date = new Date().toUTCString(),
    sessionId?: TSessionId,
    // estimateId?: TEstimateId
    isTempEstimate?: boolean
  ) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;

    if (!estimate) {
      console.error('No saved Estimate - cannot create SessionEstimate');
      return;
    }

    try {
      this.setIsSaving(true);
      await this.q(
        SessionEstimateService.createSessionEstimate({
          estimateId: estimate.id,
          title,
          date,
          sessionId,
        })
      );

      await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  updateSessionEstimate = async (
    sessionEstimateId: TSessionEstimateId,
    title?: string,
    date?: Nullable<string>,
    sessionId?: TSessionId,
    isTempEstimate?: boolean
  ) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;

    if (!estimate) {
      console.error('No saved Estimate - cannot update SessionEstimate');
      return;
    }

    try {
      this.setIsSaving(true);
      await this.q(
        SessionEstimateService.updateSessionEstimate(sessionEstimateId, {
          title,
          date: date ?? undefined,
          sessionId,
        })
      );

      isTempEstimate ? await this.refreshTempEstimate() : await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  deleteSessionEstimate = async (sessionEstimateId: TSessionEstimateId, isTempEstimate?: boolean) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;

    if (!estimate) {
      console.error('No saved Estimate - cannot create SessionEstimate');
      return;
    }

    try {
      this.setIsSaving(true);
      await this.q(SessionEstimateService.deleteSessionEstimate(sessionEstimateId));

      isTempEstimate ? await this.refreshTempEstimate() : await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  /**
   * SessionEstimateItems
   */

  createSessionEstimateItem = async (
    sessionEstimateId: TSessionEstimateId,
    title = 'New Estimate Item',
    units = 1,
    unitType = SessionEstimateItemUnitType.Count,
    rate = 0,
    sessionActivityId?: TSessionActivityId,
    isTempEstimate?: boolean
  ) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;

    if (!estimate) {
      console.error('No saved Estimate - cannot create SessionEstimate');
      return;
    }

    try {
      this.setIsSaving(true);
      await SessionEstimateItemService.createSessionEstimateItem({
        sessionEstimateId,
        sessionActivityId,
        title,
        units,
        unitType,
        rate,
      });

      await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  updateSessionEstimateItem = async (
    sessionEstimateItemId: TSessionEstimateItemId,
    title?: string,
    units?: number,
    unitType?: SessionEstimateItemUnitType,
    rate?: number,
    taxesMultiplier?: number,
    handlingMultiplier?: number,
    pAndHMultiplier?: number,
    sessionActivityId?: TSessionActivityId,
    isTempEstimate?: boolean
  ) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;

    if (!estimate) {
      console.error('No saved Estimate - cannot create SessionEstimate');
      return;
    }

    try {
      this.setIsSaving(true);
      await SessionEstimateItemService.updateSessionEstimateItem(sessionEstimateItemId, {
        title,
        units,
        unitType,
        rate,
        taxesMultiplier,
        handlingMultiplier,
        pAndHMultiplier,
        sessionActivityId,
      });

      await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  deleteSessionEstimateItem = async (sessionEstimateItemId: TSessionEstimateItemId, isTempEstimate?: boolean) => {
    try {
      this.setIsSaving(true);
      const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;

      if (!estimate) {
        console.error('No saved Estimate - cannot create SessionEstimate');
        return;
      }

      await SessionEstimateItemService.deleteSessionEstimateItem(sessionEstimateItemId);

      await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  /**
   * UsageEstimates
   */

  get usageEstimateList(): UsageEstimate[] | undefined {
    return this.currentEstimate?.usage_estimates;
  }

  createUsageEstimate = async (title = 'New UsageEstimate', commercialId?: TCommercialId, isTempEstimate?: boolean) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;
    if (!estimate) {
      console.error('No saved Estimate - cannot create UsageEstimate');
      return;
    }

    try {
      await this.q(
        UsageEstimateService.createUsageEstimate({
          estimateId: estimate.id,
          title,
          commercialId,
        })
      );

      isTempEstimate ? await this.refreshTempEstimate(true) : await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    }
  };

  updateUsageEstimate = async (usageEstimateId: TUsageEstimateId, title?: string, isTempEstimate?: boolean) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;
    if (!estimate) {
      console.error('No saved Estimate - cannot update UsageEstimate');
      return;
    }

    try {
      this.setIsSaving(true);
      await this.q(
        UsageEstimateService.updateUsageEstimate(usageEstimateId, {
          title,
        })
      );

      isTempEstimate ? await this.refreshTempEstimate(true) : await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  deleteUsageEstimate = async (usageEstimateId: TUsageEstimateId, isTempEstimate?: boolean) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;
    if (!estimate) {
      console.error('No saved Estimate - cannot delete UsageEstimate');
      return;
    }

    try {
      this.setIsSaving(true);
      await this.q(UsageEstimateService.deleteUsageEstimate(usageEstimateId));

      isTempEstimate ? await this.refreshTempEstimate(true) : await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  /**
   * Usage Estimate Items
   */

  createUsageEstimateItem = async (newUsageEstimateItem: TCreateUsageEstimateItemRequest, isTempEstimate?: boolean) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;
    if (!estimate) {
      console.error('No saved Estimate - cannot create UsageEstimateItem');
      return;
    }

    try {
      this.setIsSaving(true);
      await UsageEstimateItemService.createUsageEstimateItem(newUsageEstimateItem);

      isTempEstimate ? await this.refreshTempEstimate(true) : await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  updateUsageEstimateItem = async (
    usageEstimateItemId: TUsageEstimateItemId,
    updatedUsageEstimateItem: TUpdateUsageEstimateItemRequest,
    isTempEstimate?: boolean
  ) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;
    if (!estimate) {
      console.error('No saved Estimate - cannot update UsageEstimateItem');
      return;
    }

    try {
      this.setIsSaving(true);
      const { usageEstimateItem } = await this.q(
        UsageEstimateItemService.updateUsageEstimateItem(usageEstimateItemId, updatedUsageEstimateItem)
      );

      isTempEstimate ? await this.refreshTempEstimate(true) : await this.refreshEstimate(estimate.id);

      return usageEstimateItem;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  deleteUsageEstimateItem = async (usageEstimateItemId: TUsageEstimateItemId, isTempEstimate?: boolean) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;
    if (!estimate) {
      console.error('No saved Estimate - cannot delete UsageEstimateItem');
      return;
    }

    try {
      this.setIsSaving(true);
      await UsageEstimateItemService.deleteUsageEstimateItem(usageEstimateItemId);

      isTempEstimate ? await this.refreshTempEstimate(true) : await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };
}
